import React, { useState, useEffect } from 'react';
import { Tabs, Dropdown, Pagination, Modal } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearYourTownloopSearchValue } from '@/store/slices/searchBarSlice';
import { eventBus } from '@/utils/eventBus';
import { getMerchantDashboardCount } from '@/api/store';
import { getMerchantById } from '@/api/user';
import styles from './YourStore.module.scss';
import icon1 from '@/assets/images/路径 5.png';
import { useTranslation } from 'react-i18next';

const YourStore: React.FC = () => {
  const dispatch = useAppDispatch();

  //yourTownloopSearchValue关键字
  const yourTownloopSearchValue = useAppSelector((state) => state.searchBar.yourTownloopSearchValue);
  //国际化语言切换
  const { t, i18n } = useTranslation();
  // 获取用户信息
  const userInfo = useAppSelector((state) => state.user.userInfo);
  const merchantId = userInfo?.merchant_id || '';

  // 新增状态管理
  const [metricModalVisible, setMetricModalVisible] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  
  // 店铺仪表盘数据状态
  const [dashboardData, setDashboardData] = useState<any>({
    product_order_paid_amount: 0,
    product_order_count: 0,
    product_order_refund_amount: 0,
    product_order_refund_count: 0,
    service_order_paid_amount: 0,
    service_order_count: 0,
    service_order_refund_amount: 0,
    service_order_refund_count: 0,
    total_order_count: 0,
    total_order_amount: 0,
    total_refund_amount: 0,
    total_account_amount: 0,
    user_follow_count: 0
  });

  // 商户信息状态
  const [merchantInfo, setMerchantInfo] = useState<any>({
    merchant_name: 'Store Name',
    merchant_logo_file_url: ''
  });

  // 获取店铺仪表盘数据
  const fetchDashboardData = async () => {
    try {
      const res = await getMerchantDashboardCount({});
      if (res.code === 200) {
        setDashboardData(res.data);
      }
    } catch (error) {
      console.error('获取店铺仪表盘数据失败:', error);
    }
  };

  // 获取商户信息
  const fetchMerchantInfo = async () => {
    if (!merchantId) {
      console.log('No merchant_id available');
      return;
    }

    try {
      const response = await getMerchantById({ merchant_id: merchantId });
      console.log('Merchant info response:', response);
      
      if (response.code === 200 && response.data) {
        setMerchantInfo({
          merchant_name: response.data.merchant_name || 'Store Name',
          merchant_logo_file_url: response.data.merchant_logo_file_url || ''
        });
      }
    } catch (error) {
      console.error('获取商户信息失败:', error);
    }
  };

  //mounted 和unmounted
  useEffect(() => {
    // 组件挂载时获取数据
    fetchDashboardData();
    fetchMerchantInfo();
    
    return () => {
      console.log('组件销毁时');
      //销毁清除townloopSearchValue的值
      eventBus.emit('deleteTownloopSearchValue');
      dispatch(clearYourTownloopSearchValue());
    }
  }, [merchantId]);

  //yourTownloopSearchValue关键字的监听
  useEffect(() => {
    console.log('YourStore组件yourTownloopSearchValue', yourTownloopSearchValue);
  }, [yourTownloopSearchValue])


  // 根据API数据动态生成所有指标
  const generateMetricsFromApiData = () => {
    const apiFieldMapping = {
      'product_order_paid_amount': t("StoreParameters.ProductOrderPaidAmount"),
      'product_order_count': t("StoreParameters.ProductOrderCount"),
      'product_order_refund_amount': t("StoreParameters.ProductOrderRefundAmount"),
      'product_order_refund_count': t("StoreParameters.ProductOrderRefundCount"),
      'service_order_paid_amount': t("StoreParameters.ServiceOrderPaidAmount"),
      'service_order_count': t("StoreParameters.ServiceOrderCount"),
      'service_order_refund_amount': t("StoreParameters.ServiceOrderRefundAmount"),
      'service_order_refund_count': t("StoreParameters.ServiceOrderRefundCount"),
      'total_order_count': t("StoreParameters.TotalOrderCount"),
      'total_order_amount': t("StoreParameters.TotalOrderAmount"),
      'total_refund_amount': t("StoreParameters.TotalRefundAmount"),
      'total_account_amount': t("StoreParameters.TotalAccountAmount"),
      'user_follow_count': t("StoreParameters.UserFollowCount")
    };

    return Object.entries(dashboardData).map(([key, value]) => {
      const displayName = apiFieldMapping[key as keyof typeof apiFieldMapping] || key;
      const formattedValue = typeof value === 'number' 
        ? (key.includes('amount') ? `$${value}` : `${value}`)
        : `${value}`;
      
      return {
        name: displayName,
        current: formattedValue
      };
    });
  };

  // 动态生成performanceMetrics - 显示所有API数据
  const performanceMetrics = generateMetricsFromApiData();


  // 商店信息数据
  const storeInfo = {
    overallScore: '4.8/5.0',
    serviceGuarantee: t('yourStore.ServiceGuaranteeContent'),//'Support 7-Day No-Reason Return',
    deliverySpeed: t('yourStore.DeliverySpeedContent'),//'98% (24-Hour Shipping Rate)',
    qualityScore: '4.9',
    reputationLevel: 'N/A',
    storeDeposit: t('yourStore.StoreDepositContent'),//'$2,000 (Refundable)'
  };

  return (
    <div className={styles['your-store-box']}>
      <div className={styles['store-parameters-container']}>
        <div className={styles['content-section']}>
          {/* 左侧 */}
          <div className={styles['metrics-section']}>
          <div className={styles['header-section']}>
          <div className={styles['header-row']}>
            <div className={styles['left-section']}>
              <div className={styles['title-time-row']}>
                <h1 className={styles['page-title']}>{t('yourStore.StoreParameters')}</h1>
                <div className={styles['last-updated']}>
                {t('yourStore.DataUpdated')} : 07/03/2025 14:00:00
                </div>
              </div>
            </div>
          </div>
        </div>
            <div className={styles['metrics-grid']}>
              {performanceMetrics.map((metric, index) => (
                <div key={index} className={styles['metric-card']}>
                  <div className={styles['metric-name']}>{metric.name}</div>
                  <div className={styles['metric-values']}>
                    <div className={styles['current-value']}>{metric.current}</div>
                    {/* <div className={styles['yesterday-value']}>
                      Yesterday: {metric.yesterday}
                    </div> */}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* 右侧商店信息 */}
          <div className={styles['store-info-section']}>
            <div className={styles['store-info-card']}>
              <div className={styles['store-header']}>
                <div className={styles['store-icon']}>
                  <div className={styles['icon-container']}>
                    {merchantInfo.merchant_logo_file_url ? (
                      <img 
                        src={merchantInfo.merchant_logo_file_url} 
                        alt="Store Logo" 
                        className={styles['store-logo']}
                      />
                    ) : (
                      <div className={styles['store-icon-symbol']}></div>
                    )}
                  </div>
                </div>
                <div className={styles['store-title']}>
                  <div className={styles['store-name']}>{merchantInfo.merchant_name}</div>
                  <div className={styles['store-subtitle']}>{t('yourStore.StoreInformation')}</div>
                </div>
              </div>
              
              <div className={styles['store-details']}>
                <div className={styles['detail-item']}>
                  <span className={styles['detail-label']}>{t('yourStore.OverallScore')}</span>
                  <span className={styles['detail-value']}>{storeInfo.overallScore}</span>
                </div>
                <div className={styles['detail-item']}>
                  <span className={styles['detail-label']}>{t('yourStore.QualityScore')}</span>
                  <span className={styles['detail-value']}>{storeInfo.qualityScore}</span>
                </div>
                <div className={styles['detail-item']}>
                  <span className={styles['detail-label']}>{t('yourStore.ReputationLevel')}</span>
                  <span className={styles['detail-value']}>{storeInfo.reputationLevel}</span>
                </div>
                <div className={styles['detail-item']}>
                  <span className={styles['detail-label']}>{t('yourStore.ServiceGuarantee')}</span>
                  <span className={styles['detail-value']}>{storeInfo.serviceGuarantee}</span>
                </div>
                <div className={styles['detail-item']}>
                  <span className={styles['detail-label']}>{t('yourStore.DeliverySpeed')}</span>
                  <span className={styles['detail-value']}>{storeInfo.deliverySpeed}</span>
                </div>
                <div className={styles['detail-item']}>
                  <span className={styles['detail-label']}>{t('yourStore.StoreDeposit')}</span>
                  <span className={styles['detail-value']}>{storeInfo.storeDeposit}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YourStore; 

